
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Task, TaskPriority, ChecklistItem, RecurrencePattern } from '@/types/lead';
import TaskForm from './TaskForm';
import { getTaskCategoryOptions } from '@/services/leads/taskService';
import TaskChecklist from '@/components/tasks/TaskChecklist';
import RecurrenceSelector from '@/components/tasks/RecurrenceSelector';

interface EditTaskDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  task: Task;
  onSave: (taskId: string, updates: Partial<Task>) => void;
  priorityOptions: { value: string; label: string }[];
}

const EditTaskDialog: React.FC<EditTaskDialogProps> = ({
  isOpen,
  setIsOpen,
  task,
  onSave,
  priorityOptions,
}) => {
  const [title, setTitle] = React.useState(task.title);
  const [description, setDescription] = React.useState(task.description || '');
  const [dueDate, setDueDate] = React.useState(task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 16) : '');
  const [priority, setPriority] = React.useState(task.priority);
  const [assignedTo, setAssignedTo] = React.useState(task.assignedTo || '');
  const [categories, setCategories] = React.useState(task.categories || []);
  const [reminderTimes, setReminderTimes] = React.useState<string[]>(task.reminderTimes || []);
  const [checklist, setChecklist] = React.useState<ChecklistItem[]>(task.checklist || []);
  const [recurrencePattern, setRecurrencePattern] = React.useState<RecurrencePattern | null>(task.recurrencePattern || null);
  const [recurrenceInterval, setRecurrenceInterval] = React.useState(task.recurrenceInterval || 1);
  const [recurrenceEndDate, setRecurrenceEndDate] = React.useState<string | null>(task.recurrenceEndDate || null);

  // Create wrapper functions to handle the type conversions safely
  const handleSetPriority = (value: string) => {
    setPriority(value as TaskPriority);
  };

  const handleSave = () => {
    onSave(task.id, {
      title,
      description,
      dueDate: dueDate ? new Date(dueDate) : undefined,
      priority,
      assignedTo: assignedTo || undefined,
      categories,
      reminderTimes,
      checklist,
      recurrencePattern,
      recurrenceInterval,
      recurrenceEndDate,
    });
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Task</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <TaskForm
            newTaskTitle={title}
            setNewTaskTitle={setTitle}
            newTaskDescription={description}
            setNewTaskDescription={setDescription}
            newTaskDueDate={dueDate}
            setNewTaskDueDate={setDueDate}
            newTaskPriority={priority}
            setNewTaskPriority={handleSetPriority}
            newTaskAssignedTo={assignedTo}
            setNewTaskAssignedTo={setAssignedTo}
            priorityOptions={priorityOptions}
            newTaskCategories={categories}
            setNewTaskCategories={setCategories}
            newTaskReminderTimes={reminderTimes}
            setNewTaskReminderTimes={setReminderTimes}
          />
          <div className="mt-4 border-t pt-4">
            <TaskChecklist items={checklist} onChange={setChecklist} />
          </div>
          <div className="mt-4 border-t pt-4">
            <RecurrenceSelector
              pattern={recurrencePattern}
              interval={recurrenceInterval}
              endDate={recurrenceEndDate}
              onChange={({ pattern, interval, endDate }) => {
                setRecurrencePattern(pattern);
                setRecurrenceInterval(interval);
                setRecurrenceEndDate(endDate);
              }}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditTaskDialog;
